/**
 * Design System Theme Constants
 * Based on the new design model with red/pink accent and glass morphism
 */

export const THEME = {
  // Backgrounds
  bg: '#050505',
  bgSurface: '#0a0a0a',
  glass: 'rgba(12, 12, 12, 0.7)',
  glassLight: 'rgba(18, 18, 18, 0.8)',
  glassSurface: 'rgba(10, 10, 10, 0.6)',

  // Borders
  border: 'rgba(255, 255, 255, 0.06)',
  borderHover: 'rgba(255, 255, 255, 0.12)',
  borderFocus: 'rgba(255, 255, 255, 0.18)',

  // Accent colors (red/pink gradient theme)
  accent: 'linear-gradient(135deg, #FF3366 0%, #BA002E 100%)',
  accentColor: '#FF3366',
  accentDark: '#BA002E',
  accentSoft: 'rgba(255, 51, 102, 0.1)',
  accentBorder: 'rgba(255, 51, 102, 0.25)',
  accentHover: 'rgba(255, 51, 102, 0.15)',

  // Text colors
  textMain: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.5)',
  textMuted: 'rgba(255, 255, 255, 0.3)',

  // Status colors
  success: '#10B981',
  successSoft: 'rgba(16, 185, 129, 0.1)',
  warning: '#F59E0B',
  warningSoft: 'rgba(245, 158, 11, 0.1)',
  error: '#EF4444',
  errorSoft: 'rgba(239, 68, 68, 0.1)',
  info: '#3B82F6',
  infoSoft: 'rgba(59, 130, 246, 0.1)',

  // Shadows
  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.1)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.2)',
  shadowXl: '0 20px 40px rgba(0, 0, 0, 0.4)',
  shadowAccent: '0 4px 20px rgba(255, 51, 102, 0.3)',
  shadowAccentLg: '0 8px 30px rgba(255, 51, 102, 0.4)',

  // Transitions
  transitionFast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  transitionNormal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  transitionSlow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',

  // Border radius
  radiusSm: '8px',
  radiusMd: '10px',
  radiusLg: '12px',
  radiusXl: '16px',
  radius2xl: '20px',

  // Spacing scale (complementing Tailwind)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
  },
} as const;

/**
 * Provider color mapping for visual identification
 */
export const PROVIDER_COLORS = {
  openai: {
    bg: 'rgba(16, 163, 127, 0.1)',
    border: 'rgba(16, 163, 127, 0.3)',
    text: '#10a37f',
  },
  anthropic: {
    bg: 'rgba(245, 158, 11, 0.1)',
    border: 'rgba(245, 158, 11, 0.3)',
    text: '#f59e0b',
  },
  google: {
    bg: 'rgba(66, 133, 244, 0.1)',
    border: 'rgba(66, 133, 244, 0.3)',
    text: '#4285f4',
  },
  grok: {
    bg: 'rgba(168, 85, 247, 0.1)',
    border: 'rgba(168, 85, 247, 0.3)',
    text: '#a855f7',
  },
  meta: {
    bg: 'rgba(99, 102, 241, 0.1)',
    border: 'rgba(99, 102, 241, 0.3)',
    text: '#6366f1',
  },
  deepseek: {
    bg: 'rgba(6, 182, 212, 0.1)',
    border: 'rgba(6, 182, 212, 0.3)',
    text: '#06b6d4',
  },
  qwen: {
    bg: 'rgba(236, 72, 153, 0.1)',
    border: 'rgba(236, 72, 153, 0.3)',
    text: '#ec4899',
  },
} as const;

/**
 * Status badge colors
 */
export const STATUS_COLORS = {
  pending: {
    bg: THEME.warningSoft,
    border: 'rgba(245, 158, 11, 0.3)',
    text: THEME.warning,
  },
  processing: {
    bg: THEME.infoSoft,
    border: 'rgba(59, 130, 246, 0.3)',
    text: THEME.info,
  },
  completed: {
    bg: THEME.successSoft,
    border: 'rgba(16, 185, 129, 0.3)',
    text: THEME.success,
  },
  failed: {
    bg: THEME.errorSoft,
    border: 'rgba(239, 68, 68, 0.3)',
    text: THEME.error,
  },
  recommended: {
    bg: THEME.accentSoft,
    border: THEME.accentBorder,
    text: THEME.accentColor,
  },
} as const;

/**
 * CSS custom properties for use in components
 * Can be used for dynamic theming if needed in the future
 */
export const CSS_VARS = `
  --color-bg: ${THEME.bg};
  --color-bg-surface: ${THEME.bgSurface};
  --color-glass: ${THEME.glass};
  --color-border: ${THEME.border};
  --color-accent: ${THEME.accentColor};
  --color-text-main: ${THEME.textMain};
  --color-text-secondary: ${THEME.textSecondary};
  --color-text-muted: ${THEME.textMuted};
  --transition-normal: ${THEME.transitionNormal};
  --radius-md: ${THEME.radiusMd};
  --radius-lg: ${THEME.radiusLg};
  --shadow-accent: ${THEME.shadowAccent};
`;

/**
 * Z-index scale for consistent layering
 */
export const Z_INDEX = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  modal: 40,
  overlay: 50,
  tooltip: 60,
} as const;
